import { z } from 'zod';

export const ApiErrorCode = z.enum([
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'VALIDATION_FAILED',
  'CONFLICT',
  'RATE_LIMITED',
  'INTERNAL',
  /** Source PDF needs an open password the server wasn't given (or got
   *  the wrong one). The client should prompt for it and retry. */
  'PDF_PASSWORD_REQUIRED',
  /** Signed in, but the account's email and/or phone is not confirmed yet
   *  (0245). Distinct from FORBIDDEN because the client's remedy is
   *  different: FORBIDDEN is a wall, this is a door — the UI shows the
   *  confirm-your-phone step and retries the original request afterwards. */
  'CONTACT_UNVERIFIED',
  /** Email-link proof is valid; a new website login still needs its required profile. */
  'REGISTRATION_PROFILE_REQUIRED',
  /** A contact's phone was set to one of YRP's own phone lines (any
   *  `voice_phone_numbers` row) — 422 from the CRM contact and phone routes.
   *  Distinct from VALIDATION_FAILED because the number is real, just ours:
   *  the remedy is to save the caller's number (the From number on an
   *  inbound call), and an AI client needs to tell that apart from a typo.
   *  See ADR 0019. */
  'YRP_PHONE_LINE',
  /** 410 — the dashboard no longer makes this Lone Wolf request because
   *  Lone Wolf has not published it for partners. On 2026-10-22 Lone Wolf
   *  required YRP to use only the API documented for it, which ended the
   *  internal forms-editor ("SPA") calls behind in-dashboard form filling.
   *  Distinct from INTERNAL/CONFLICT because no retry or fix on our side can
   *  help: the remedy is to do that step on Lone Wolf's own website. Thrown
   *  by `TransactUnsupportedRouteError` in `@yrp/lonewolf-transact`. */
  'LONEWOLF_UNSUPPORTED_ROUTE',
  /** 503: a form copy that goes out (signing, download) would need YRP's
   *  values printed on it, and the server could not load the typeface Lone
   *  Wolf prints in (the bundled Courier New Bold: missing, changed or
   *  unreadable). YRP never prints those values in another typeface, so
   *  nothing was sent or downloaded. Distinct from INTERNAL because the
   *  remedy is a server fix, not a retry with other input (ADR 0021). */
  'FORM_FONT_UNAVAILABLE',
]);
export type ApiErrorCode = z.infer<typeof ApiErrorCode>;

export const ApiError = z.object({
  code: ApiErrorCode,
  message: z.string(),
  details: z.unknown().optional(),
});
export type ApiError = z.infer<typeof ApiError>;

/**
 * The status an upstream (vendor) failure leaves the API with.
 *
 * DigitalOcean's edge replaces a 502, 503 or 504 from the app — even a JSON
 * `ApiError` sent with `Accept: application/json` — with its own 504 HTML
 * page (`x-do-orig-status` carries the original). The vendor's message, the
 * only thing that says *why* Lone Wolf or Graph refused, never reaches the
 * browser: every failure reads "HTTP 504". So the API sends those bodies as
 * 424 Failed Dependency instead, which the edge passes through untouched,
 * and echoes the original status in `x-yrp-orig-status`. See
 * `apps/api/src/plugins/upstreamStatus.ts`.
 */
export const UPSTREAM_FAILURE_STATUS = 424;

/**
 * Statuses DigitalOcean's edge does not pass through: it swaps the body for
 * its own 504 HTML page whatever the content type.
 *
 * 503 was left out at first on the assumption that it passed. It does not
 * (checked on production 2026-10-08: the API's 503 arrived as `504
 * text/html` with `x-do-orig-status: 503`), so every "connect Microsoft" /
 * "not configured" answer read "HTTP 504" — on the mobile Calendar and
 * Email tabs, in front of App Review.
 */
export const EDGE_MASKED_STATUSES: ReadonlySet<number> = new Set([502, 503, 504]);

/** Carries the status the API meant when it sent the body as a 424. */
export const ORIGINAL_STATUS_HEADER = 'x-yrp-orig-status';

/**
 * The status the API meant, recovered from the wire.
 *
 * The 424 is a transport detail between the API and its own clients, so
 * every client wrapper (dashboard, both mobile apps, the MCP gateway) runs
 * the response through this before anything branches on the status. A
 * screen that treats 503 as "connect your Microsoft account" keeps working
 * without knowing the edge exists. Only a 424 that names one of the masked
 * statuses is decoded; a plain 424, or a header with anything else in it,
 * is left as it arrived.
 */
export function intendedStatus(
  status: number,
  originalStatusHeader: string | null | undefined,
): number {
  if (status !== UPSTREAM_FAILURE_STATUS || originalStatusHeader == null) return status;
  const value = originalStatusHeader.trim();
  if (!/^\d{3}$/.test(value)) return status;
  const original = Number(value);
  return EDGE_MASKED_STATUSES.has(original) ? original : status;
}

/**
 * What a Microsoft-backed route (calendar, mail, Teams, files) answers when
 * it has no usable Graph token for the caller: never connected, the grant
 * expired or was revoked, or Graph is not configured on the server. The
 * remedy is the same in each case — connect Microsoft 365 on the dashboard
 * Settings page — so clients show that step instead of an error. Read it off
 * a status that went through `intendedStatus`.
 */
export const MICROSOFT_NOT_CONNECTED_STATUS = 503;

/**
 * True when a status means "the thing we depend on failed", i.e. a retry
 * from the client cannot help and the message should be shown as the
 * vendor's, not the dashboard's. Covers the rewritten 424 as well as any
 * 5xx that still comes through (a real timeout, an unhandled error).
 */
export function isUpstreamFailureStatus(status: number | undefined | null): boolean {
  if (status == null) return false;
  return status === UPSTREAM_FAILURE_STATUS || status >= 500;
}
